import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { s } from '../../components/PeriodStatsPage.styles';
import UsageModal from '../../components/filter/UsageModal';
import axios from 'axios';
import MapService from '../../../../service/map/Map';

interface RegionProps {
  code: string;
  name: string;
  lat: number;
  lng: number;
}

export interface SearchResult {
  name: string;
  caseNumber: string;
  address: string;
  lat?: number;
  lng?: number;
  category_group?: string;
}

export const SearchModal = ({
  isOpen,
  onClose,
  value,
  onChangeOption,
}: {
  isOpen: boolean;
  onClose: () => void;
  value: SearchResult | null;
  onChangeOption: (val: SearchResult) => void;
}) => {
  const [year, setYear] = useState<string>('');
  const [eventNumber, setEventNumber] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCase, setSelectedCase] = useState<SearchResult | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [searchMode, setSearchMode] = useState<'case' | 'address'>('address');
  const [detailAddress, setDetailAddress] = useState('');
  const [ctpList, setCtpList] = useState<RegionProps[]>([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedTypeDetail, setSelectedTypeDetail] = useState('');
  const [originalResults, setOriginalResults] = useState<SearchResult[]>([]);
  const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);

  const getRegion = async (regionCode: string) => {
    try {
      const res = await MapService.regionList(regionCode);
      return res.data as RegionProps[];
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const handleClose = () => {
    onClose();
    setSearchResults([]);
    setEventNumber('');
    setSelectedCase(null);
    setSelectedIndex(null);
    setSelectedTypeDetail('');
    setIsUsageModalOpen(false);
  };

  const fetchAddressResults = async (query: string) => {
    if (!query) return;
    setLoading(true);
    try {
      const apiHost = process.env.REACT_APP_HOST_API?.replace(/\/$/, '') + '/';
      const response = await axios.get(`${apiHost}stat/search`, {
        params: { search: query },
      });
      const data = response.data?.data ?? [];
      const results: SearchResult[] = data
        .filter((item: any) => item.category_group)
        .map((item: any) => ({
          address: item.address,
          name: item.category_group,
          category_group: item.category_group,
          lat: item.lat,
          lng: item.lng,
        }));

      setOriginalResults(results);

      let filtered = results;
      if (selectedTypeDetail) {
        filtered = filtered.filter(
          (r: SearchResult) => r.category_group === selectedTypeDetail,
        );
      }

      setSearchResults(filtered);

      setSelectedCase(null);
      setSelectedIndex(null);
    } catch (err) {
      console.error('주소 검색 오류:', err);
      setSearchResults([]);
      setOriginalResults([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchResults = async (query: string) => {
    setLoading(true);
    try {
      const apiHost = process.env.REACT_APP_HOST_API?.replace(/\/$/, '') + '/';
      const response = await axios.get(`${apiHost}search`, {
        params: { search: query },
      });
      const data = response.data?.data ?? [];
      const results = data
        .filter((item: any) => item.category_group)
        .map((item: any) => ({
          name: item.category_group,
          category_group: item.category_group,
          caseNumber: item.caseno,
          address: item.addr,
          lat: item.lat,
          lng: item.lng,
        }));

      setOriginalResults(results);

      let filtered = results;
      if (selectedTypeDetail) {
        filtered = filtered.filter(
          (r: SearchResult) => r.category_group === selectedTypeDetail,
        );
      }

      setSearchResults(filtered);
      setSelectedCase(null);
      setSelectedIndex(null);
    } catch (err) {
      console.error('검색 오류:', err);
      setSearchResults([]);
      setOriginalResults([]);
    } finally {
      setLoading(false);
    }
  };

  // 사건번호 검색
  useEffect(() => {
    if (searchMode !== 'case' || !eventNumber) {
      setSearchResults([]);
      setSelectedCase(null);
      setSelectedIndex(null);
      return;
    }

    const timer = setTimeout(() => {
      const caseNo = `${year}타경${eventNumber}`;
      fetchSearchResults(caseNo);
    }, 300);

    return () => clearTimeout(timer);
  }, [eventNumber, year, searchMode, selectedType, selectedTypeDetail]);

  // 주소 검색
  useEffect(() => {
    if (searchMode !== 'address') return;
    if (!detailAddress) {
      setSearchResults([]);
      setSelectedCase(null);
      setSelectedIndex(null);
      return;
    }

    fetchAddressResults(detailAddress);
  }, [searchMode, detailAddress, selectedTypeDetail]);

  useEffect(() => {
    if (isOpen) {
      setSelectedCase(null);
      setEventNumber('');
      setSearchResults([]);
      setSelectedIndex(null);
      setDetailAddress('');
      setYear('');
      setSelectedType('');
      setSelectedTypeDetail('');
      setIsUsageModalOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    getRegion('').then(setCtpList);
  }, []);

  useEffect(() => {
    if (!originalResults.length) return;

    let filtered = originalResults;
    if (selectedTypeDetail) {
      filtered = filtered.filter(
        (r: SearchResult) => r.category_group === selectedTypeDetail,
      );
    }
    setSearchResults(filtered);
  }, [selectedTypeDetail, originalResults]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const renderSearchResults = () =>
    searchResults.map((item, idx) => (
      <li key={idx}>
        <CaseInfo
          selected={selectedIndex === idx}
          onClick={() => {
            setSelectedIndex(idx);
            setSelectedCase(item);
            console.log('[Modal] 선택된 값:', item);
          }}
        >
          {searchMode === 'case' ? (
            <>
              <CaseInfoInner>
                <CaseType>{item.name}</CaseType>
                <CaseNumber>{item.caseNumber}</CaseNumber>
              </CaseInfoInner>
              <CaseAddress>{item.address}</CaseAddress>
            </>
          ) : (
            <>
              <CaseInfoInner>
                {item.name !== undefined && <CaseType>{item.name}</CaseType>}
              </CaseInfoInner>
              <CaseAddress>{item.address}</CaseAddress>
            </>
          )}
          {selectedIndex === idx && (
            <CheckImg src="assets/images/icon/check.png" alt="선택됨" />
          )}
        </CaseInfo>
      </li>
    ));

  const getPlaceholderMessage = () => {
    if (searchMode === 'case') {
      return !eventNumber
        ? '통계를 확인할 물건의 사건번호를 검색해주세요.'
        : loading
          ? '검색 중...'
          : searchResults.length === 0
            ? '검색 결과가 없습니다.'
            : '';
    }
    if (searchMode === 'address') {
      if (!detailAddress) return '통계를 확인할 물건의 주소를 검색해주세요.';
      if (loading) return '검색 중...';
      if (!loading && searchResults.length === 0)
        return '검색 결과가 없습니다.';
    }
    return '';
  };

  return (
    <ModalOverlay>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <Header>
          <CloseButton onClick={handleClose}>
            <img src="/assets/images/arrow/arrow_down.svg" alt="닫기" />
          </CloseButton>
          <h3>물건 검색</h3>
        </Header>

        <TabWrapper>
          <s.TabRow style={{ width: '100%' }}>
            <s.TabPill
              $active={searchMode === 'address'}
              onClick={() => setSearchMode('address')}
              style={{ width: '100%' }}
            >
              주소
            </s.TabPill>
            <s.TabPill
              $active={searchMode === 'case'}
              onClick={() => setSearchMode('case')}
              style={{ width: '100%' }}
            >
              사건번호
            </s.TabPill>
          </s.TabRow>
        </TabWrapper>

        {searchMode === 'case' && (
          <ControlGroup>
            <Label>사건번호</Label>
            <CaseInputRow>
              <EventInput
                style={{ flex: 1 }}
                placeholder="연도"
                value={year}
                onChange={(e) => {
                  const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
                  setYear(onlyNumbers);
                }}
              />
              <InputGroup style={{ display: 'flex', flex: 3 }}>
                <CaseTypeLabel>타경</CaseTypeLabel>
                <EventInput
                  style={{ flex: 1 }}
                  placeholder="사건번호"
                  value={eventNumber}
                  onChange={(e) => setEventNumber(e.target.value)}
                />
              </InputGroup>
            </CaseInputRow>
          </ControlGroup>
        )}

        {searchMode === 'address' && (
          <ControlGroupAddress>
            <AddressFieldGroup>
              <AddressLabel>
                지역
                <EventInputAddr
                  placeholder="상세주소 입력"
                  value={detailAddress}
                  onChange={(e) => setDetailAddress(e.target.value)}
                />
              </AddressLabel>

              <AddressLabel>
                물건 종류
                <TypeButton onClick={() => setIsUsageModalOpen(true)}>
                  <span>
                    {selectedTypeDetail
                      ? selectedTypeDetail
                      : selectedType || '전체'}
                  </span>
                  <img
                    src="/assets/images/arrow/arrow_right.svg"
                    alt=""
                    style={{
                      width: 20,
                      height: 20,
                      transform: 'rotate(90deg)',
                    }}
                  />
                </TypeButton>
                {isUsageModalOpen && (
                  <UsageModal
                    usage={selectedType}
                    setUsage={setSelectedType}
                    usageDetail={selectedTypeDetail}
                    setUsageDetail={setSelectedTypeDetail}
                    useTypeList={true}
                    onClose={() => setIsUsageModalOpen(false)}
                  />
                )}
              </AddressLabel>
            </AddressFieldGroup>
          </ControlGroupAddress>
        )}

        <SearchResult>
          {getPlaceholderMessage() ? (
            <p>{getPlaceholderMessage()}</p>
          ) : (
            <ul>{renderSearchResults()}</ul>
          )}
        </SearchResult>

        <ButtonContainer>
          <ApplyBtn
            type="button"
            disabled={!selectedCase}
            onClick={(e) => {
              if (!selectedCase) return;
              onChangeOption(selectedCase);
              handleClose();
            }}
          >
            물건 통계 보기
          </ApplyBtn>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

const TabWrapper = styled.div`
  width: 360;
  height: 60;
  angle: 0 deg;
  opacity: 1;
  top: 94px;
  gap: 8px;
  padding-top: 8px;
  padding-right: 16px;
  padding-bottom: 8px;
  padding-left: 16px;
  border-bottom-width: 1px;
`;

const Label = styled.div`
  margin-bottom: 8px;
`;

const CaseInputRow = styled.div`
  display: flex;
  width: 100%;
  gap: 4px;
`;

const CaseTypeLabel = styled.span`
  margin-right: 4px;
  line-height: 32px;
`;

const ControlGroupAddress = styled.div`
  font-weight: 500;
  font-size: 14px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: -2%;

  flex-direction: column;
  padding: 16px;
  align-items: flex-start;
  display: flex;
`;

const AddressFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const AddressLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  max-width: 900px;
  min-height: 100vh;
  overflow-x: hidden;
  margin: 0 auto;
  inset: 0;
  background: #fff;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  padding: 6px;
  border-radius: 8px;
  height: full;
  overflow: hidden;
`;

const Header = styled.div`
  angle: 0 deg;
  opacity: 1;
  top: 38px;
  gap: 2px;
  padding-top: 6px;
  padding-right: 6px;
  padding-bottom: 6px;
  padding-left: 16px;
  border-bottom-width: 1px;

  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ControlGroup = styled.div`
  font-weight: 500;
  font-size: 14px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: -2%;
  padding: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 20px 0;

  * {
    min-width: 0;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const EventInput = styled.input`
  height: 44px;
  background: #f4f5f7;
  border: none;
  border-radius: 4px;
  padding: 0 12px;
  font-size: 14px;
`;

const EventInputAddr = styled.input`
  height: 44px;
  background: #f4f5f7;
  border: none;
  border-radius: 4px;
  padding: 0 12px;
  font-size: 14px;
`;

const SearchResult = styled.div`
  padding-right: 16px;
  padding-left: 16px;

  box-sizing: border-box;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  p {
    text-align: center;
    font-weight: 500;
    font-size: 15px;
    line-height: 22px;
    vertical-align: middle;
    color: #787d87;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    padding: 5px 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;

  img {
    width: 24px;
    rotate: 90deg;
  }
`;

const ButtonContainer = styled.div`
  angle: 0 deg;
  opacity: 1;
  gap: 8px;
  padding-top: 16px;
  padding-right: 24px;
  padding-bottom: 24px;
  padding-left: 24px;
  border-top-width: 1px;

  background: #fff;
  height: 100px;
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const ApplyBtn = styled.button<{ disabled?: boolean }>`
  text-align: center;
  width: 100%;
  height: 44px;
  gap: 10px;
  border-radius: 8px;
  background: #000;
  color: ${(props) => (props.disabled ? '#F4F5F74A' : '#fff')};
  font-weight: 500;
  font-size: 15px;
  line-height: 22px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const CaseInfo = styled.div<{ selected?: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  height: 86px;
  border-radius: 8px;
  padding: 0 6px;
  justify-content: center;
  flex-direction: column;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? '#F4F5F7' : 'white')};

  &:hover {
    background-color: ${(props) => (props.selected ? '' : '#F4F5F7')};
  }
  &:active {
    background-color: ${(props) => (props.selected ? '' : '#E8E9ED')};
  }
  transition: background-color 0.2s;
`;

const CaseInfoInner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const CaseType = styled.div`
  min-width: 52px;
  height: 24px;
  color: #fff;
  background: #5b616e;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-weight: 700;
  font-size: 13px;
  line-height: 20px;
`;

const CaseNumber = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
`;

const CaseAddress = styled.div`
  font-weight: 500;
  font-size: 15px;
  line-height: 22px;
  color: #787d87;
  margin-top: 5px;
`;

const CheckImg = styled.img`
  position: absolute;
  width: 24px;
  height: 24px;
  top: 15px;
  right: 10px;
`;

const TypeButton = styled.button`
  background: #f4f5f7;
  border: none;
  border-radius: 4px;
  min-height: 44px;
  font-size: 14px;
  padding: 0 12px;
  text-align: left;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
